import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AccountService } from '@frontendlib/common/services/account.service';
import { PermissionsService } from '@frontendlib/common/features/permissions/services/permissions.service';
import { SignalsService } from '@frontendlib/common/services/signals.service';
import { SignalType } from '@sharedlib/common/types/signal-type';
import { merge } from 'rxjs';

/**
 * Refreshes the app-wide state a subscription change invalidates, for the whole session.
 *
 * The Billing and Usage tabs read their own pushed streams from `BillingService`, but two things a
 * plan change moves are not page-local and have no other route back to the client:
 *
 * - `AccountService.getAccount()` is a cached `BehaviorSubject`, and `subscriptionStatus` is what
 *   `isTrial` is derived from on both settings tabs as well as the inactive-subscription guards.
 * - The CASL rule set decides which apps are visible (`agentView` for Stitchy, `mailAppEnabled`, …)
 *   and carries the plan's upload and storage caps, which `SubscriptionLimitsService` reads out of
 *   it. It is otherwise loaded once, at sign-in.
 *
 * Neither read is gated on a component being mounted, which is why this lives at app scope and is
 * instantiated at bootstrap rather than by whoever happens to inject it first.
 *
 * A socket reconnect refreshes both as well, because the rail has no backfill. Without that, a change
 * applied during a transport gap leaves these two behind while `BillingService` — which does
 * revalidate on reconnect — moves on, and the Billing tab renders both halves at once: "Free Trial"
 * beside a paid plan's renewal date and allowance, or a past-due banner over healthy figures.
 */
@Injectable({ providedIn: 'root' })
export class SubscriptionLiveUpdatesService {
  private readonly signalsService = inject(SignalsService);
  private readonly accountService = inject(AccountService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    merge(this.signalsService.reconnected$, this.signalsService.getSignal(SignalType.SUBSCRIPTION_UPDATED))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refresh());
  }

  /**
   * Both refreshes are best-effort, and each keeps its own previous value when the read fails —
   * `AccountService.reloadAccount` leaves the cached account alone, `loadPermissionRules` only assigns
   * on success. A stale value is what the client had a moment ago anyway, and the next reconnect or
   * sign-in reads again.
   */
  private refresh(): void {
    this.accountService.reloadAccount();
    this.permissionsService.loadPermissionRules().catch((error: unknown) => {
      console.warn('[SubscriptionLiveUpdatesService] Failed to reload permission rules', error);
    });
  }
}
